import { createContext, useContext, useState, useEffect, useCallback, ReactNode } from "react";
import { useAuth } from "@/hooks/useAuth";
import { useLocation } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useTranslation } from "react-i18next";

export type GuideStep = {
  id: string;
  title: string;
  message: string;
  action?: { label: string; path: string };
  icon?: string;
  delay?: number;
  /** Only show if this milestone is NOT yet reached */
  requiresMilestoneAbsent?: string;
  /** Only show if this milestone IS reached */
  requiresMilestone?: string;
};

type UserRole = "eleve" | "etudiant" | "enseignant" | "parent" | null;

export type Milestone =
  | "first_chat"
  | "first_challenge"
  | "first_game"
  | "five_chats"
  | "profile_complete"
  | "first_subject_click"
  | "first_photo"
  | "onboarding_done"
  | "first_class_created"
  | "first_student_added"
  | "first_class_challenge";

interface GuideContextType {
  currentTip: GuideStep | null;
  dismissTip: () => void;
  completeTip: (id: string) => void;
  completedTips: Set<string>;
  showGuide: boolean;
  toggleGuide: () => void;
  userRole: UserRole;
  isNewUser: boolean;
  /** Track a user milestone for progressive discovery */
  trackMilestone: (m: Milestone) => void;
  milestones: Set<string>;
  /** Total tips for current page */
  totalTipsForPage: number;
  /** Completed tips for current page */
  completedTipsForPage: number;
  /** Show a post-action suggestion */
  showPostAction: (tip: GuideStep) => void;
}

const GuideContext = createContext<GuideContextType>({
  currentTip: null,
  dismissTip: () => {},
  completeTip: () => {},
  completedTips: new Set(),
  showGuide: false,
  toggleGuide: () => {},
  userRole: null,
  isNewUser: false,
  trackMilestone: () => {},
  milestones: new Set(),
  totalTipsForPage: 0,
  completedTipsForPage: 0,
  showPostAction: () => {},
});

// ─── Tips per route × role ───────────────────────────────
export const TIPS: Record<string, Record<string, GuideStep[]>> = {
  "/dashboard": {
    eleve: [
      { id: "dash-welcome", title: "Bienvenue ! 🎉", message: "Voici ton tableau de bord. Tu peux suivre ta progression et accéder à toutes tes matières ici.", icon: "👋" },
      { id: "dash-chat", title: "Pose une question", message: "Tu peux discuter avec l'IA pour comprendre n'importe quel cours. Essaie maintenant !", icon: "💬", action: { label: "Aller au chat", path: "/chat" } },
      { id: "dash-challenge", title: "Teste tes connaissances", message: "Les défis te permettent de gagner des XP et de monter de niveau. Prêt ?", icon: "🏆", action: { label: "Lancer un défi", path: "/challenges" }, requiresMilestone: "first_chat" },
      { id: "dash-games", title: "Apprends en jouant", message: "Les jeux de réflexion t'aident à progresser tout en t'amusant.", icon: "🎮", action: { label: "Voir les jeux", path: "/games" }, requiresMilestone: "first_challenge" },
      { id: "dash-photo", title: "Photo de devoir 📸", message: "Tu savais ? Tu peux prendre en photo un exercice et l'IA t'expliquera la solution !", icon: "📷", action: { label: "Essayer", path: "/chat" }, requiresMilestoneAbsent: "first_photo" },
    ],
    etudiant: [
      { id: "dash-welcome", title: "Bienvenue ! 🎉", message: "Voici ton espace d'apprentissage. Explore tes matières et progresse chaque jour.", icon: "👋" },
      { id: "dash-chat", title: "L'IA est ton tuteur", message: "Pose des questions complexes, demande des explications détaillées ou des exercices.", icon: "💬", action: { label: "Aller au chat", path: "/chat" } },
      { id: "dash-challenge", title: "Défis & classement", message: "Affronte d'autres étudiants et grimpe dans le classement !", icon: "🏆", action: { label: "Lancer un défi", path: "/challenges" }, requiresMilestone: "first_chat" },
    ],
    enseignant: [
      { id: "teacher-welcome", title: "Bienvenue, Professeur ! 👨‍🏫", message: "Votre espace enseignant vous permet de gérer vos classes et suivre vos élèves.", icon: "📚" },
      { id: "teacher-class", title: "Créez votre première classe", message: "Commencez par créer une classe pour y ajouter vos élèves.", icon: "🏫", action: { label: "Créer une classe", path: "/teacher-dashboard" } },
    ],
    parent: [
      { id: "parent-welcome", title: "Bienvenue ! 👨‍👩‍👧", message: "Suivez la progression de votre enfant et ses performances.", icon: "📊" },
      { id: "parent-link", title: "Liez votre enfant", message: "Ajoutez le matricule de votre enfant pour accéder à ses résultats.", icon: "🔗", action: { label: "Ajouter un enfant", path: "/parent-dashboard" } },
    ],
  },
  "/chat": {
    eleve: [
      { id: "chat-first", title: "Demande ce que tu veux ! 📸", message: "Tu peux écrire une question, envoyer une photo de devoir, ou demander un quiz.", icon: "✨" },
      { id: "chat-photo", title: "Photo de devoir", message: "Prends en photo un exercice et l'IA t'expliquera la solution étape par étape.", icon: "📷", requiresMilestoneAbsent: "first_photo" },
      { id: "chat-voice", title: "Écoute les réponses 🔊", message: "Active la voix pour écouter les explications comme un vrai professeur !", icon: "🔊", requiresMilestone: "first_chat" },
    ],
    etudiant: [
      { id: "chat-first", title: "Ton tuteur personnel", message: "Pose des questions approfondies ou demande des exercices sur n'importe quel sujet.", icon: "🧠" },
      { id: "chat-voice", title: "Mode vocal 🔊", message: "Active la voix pour une expérience plus immersive.", icon: "🔊", requiresMilestone: "first_chat" },
    ],
    enseignant: [],
    parent: [],
  },
  "/challenges": {
    eleve: [
      { id: "challenge-start", title: "Mode défi ! 🔥", message: "Choisis une matière et un mode. Réponds vite pour gagner plus de points !", icon: "⚡" },
      { id: "challenge-multi", title: "Défi multijoueur ⚔️", message: "Défie un ami avec son matricule et comparez vos scores en temps réel !", icon: "🤺", requiresMilestone: "first_challenge" },
      { id: "challenge-class", title: "Défi de classe 🏫", message: "Ton prof peut lancer un défi pour toute la classe : réponds vite pour grimper dans le classement !", icon: "🎯", requiresMilestoneAbsent: "first_class_challenge" },
      { id: "challenge-class-done", title: "Bien joué ! 🎉", message: "Ton premier défi de classe est terminé. Consulte le classement pour voir ta place.", icon: "🏅", requiresMilestone: "first_class_challenge" },
    ],
    etudiant: [
      { id: "challenge-start", title: "Défie-toi ! 🔥", message: "Teste tes connaissances avec des quiz chronométrés et gagne des XP.", icon: "⚡" },
      { id: "challenge-multi", title: "Mode compétitif ⚔️", message: "Affronte d'autres étudiants et grimpe au classement !", icon: "🤺", requiresMilestone: "first_challenge" },
    ],
    enseignant: [],
    parent: [],
  },
  "/games": {
    eleve: [
      { id: "games-discover", title: "Jeux de réflexion 🎮", message: "Sudoku, mots mêlés, dames, Scrabble… Joue et stimule ton cerveau !", icon: "🧩" },
      { id: "games-duel", title: "Mode duel ⚔️", message: "Défie un ami dans un jeu en temps réel !", icon: "🎯", requiresMilestone: "first_game" },
    ],
    etudiant: [
      { id: "games-discover", title: "Détente intelligente 🎮", message: "Fais une pause avec des jeux qui stimulent la réflexion.", icon: "🧩" },
    ],
    enseignant: [],
    parent: [],
  },
  "/teacher-dashboard": {
    enseignant: [
      { id: "teacher-dash", title: "Votre tableau de bord 📋", message: "Gérez vos classes, créez des notions et suivez la progression de vos élèves.", icon: "📚" },
      { id: "teacher-first-class", title: "Créez votre première classe 🏫", message: "Regroupez vos élèves dans une classe pour leur assigner des notions et des défis.", icon: "🏫", requiresMilestoneAbsent: "first_class_created" },
      { id: "teacher-add-student", title: "Ajoutez des élèves 👨‍🎓", message: "Cherchez par nom ou saisissez le matricule KelasAI pour ajouter un élève à votre classe.", icon: "👨‍🎓", requiresMilestone: "first_class_created", requiresMilestoneAbsent: "first_student_added" },
      { id: "teacher-class-challenge", title: "Lancez un défi de classe ⚡", message: "Une fois vos élèves ajoutés, lancez un défi commun et suivez le classement en direct.", icon: "🏆", requiresMilestone: "first_student_added" },
      { id: "teacher-notion", title: "Créez du contenu", message: "Rédigez des notions pédagogiques et assignez-les à vos classes.", icon: "📝" },
    ],
    eleve: [],
    etudiant: [],
    parent: [],
  },
  "/parent-dashboard": {
    parent: [
      { id: "parent-dash", title: "Suivi parental 📊", message: "Consultez les scores, le temps d'activité et les recommandations pour votre enfant.", icon: "📈" },
      { id: "parent-chat-ai", title: "Chat IA pédagogique 💬", message: "Discutez avec KelasAI pour obtenir des conseils personnalisés sur l'accompagnement de votre enfant.", icon: "💬", action: { label: "Ouvrir le Chat IA", path: "/chat" } },
    ],
    eleve: [],
    etudiant: [],
    enseignant: [],
  },
};

// ─── Inactivity tips ─────────────────────────────────────
const INACTIVITY_TIPS: Record<string, GuideStep> = {
  "/dashboard": { id: "inactive-dash", title: "Besoin d'aide ? 🤔", message: "Tu sembles hésiter. Que dirais-tu de poser une question à l'IA ou de lancer un quiz ?", icon: "💡", action: { label: "Poser une question", path: "/chat" } },
  "/chat": { id: "inactive-chat", title: "Écris quelque chose !", message: "Tape ta question ou envoie une photo. L'IA est prête à t'aider !", icon: "✏️" },
  "/challenges": { id: "inactive-challenge", title: "Lance-toi !", message: "Choisis une matière pour commencer ton défi. C'est rapide et fun !", icon: "🚀" },
  "/games": { id: "inactive-games", title: "Choisis un jeu !", message: "Clique sur un jeu pour commencer. Essaie le Sudoku ou les mots mêlés !", icon: "🎮" },
};

// ─── Post-action suggestions ─────────────────────────────
export const POST_ACTION_TIPS: Record<string, GuideStep> = {
  after_first_chat: {
    id: "post-first-chat",
    title: "Bravo ! 🎉",
    message: "Ta première conversation est un succès ! Maintenant, teste tes connaissances avec un défi.",
    icon: "🏆",
    action: { label: "Lancer un défi", path: "/challenges" },
  },
  after_first_challenge: {
    id: "post-first-challenge",
    title: "Champion ! 🏆",
    message: "Tu as terminé ton premier défi ! Continue pour monter de niveau et gagner des badges.",
    icon: "⭐",
    action: { label: "Voir le classement", path: "/challenges" },
  },
  after_challenge_perfect: {
    id: "post-perfect-score",
    title: "Score parfait ! ✨",
    message: "Incroyable ! Tu as tout bon. Essaie un niveau de difficulté plus élevé !",
    icon: "🌟",
  },
  after_five_chats: {
    id: "post-five-chats",
    title: "Tu progresses vite ! 📈",
    message: "5 conversations déjà ! As-tu essayé les jeux de réflexion pour varier l'apprentissage ?",
    icon: "🎮",
    action: { label: "Voir les jeux", path: "/games" },
  },
};

const STORAGE_KEY = "kelasai_guide_completed";
const MILESTONES_KEY = "kelasai_milestones";
const MUTED_KEY = "kelasai_guide_muted";

export const GuideProvider = ({ children }: { children: ReactNode }) => {
  const { user } = useAuth();
  const location = useLocation();
  const [userRole, setUserRole] = useState<UserRole>(null);
  const [isNewUser, setIsNewUser] = useState(false);
  const [completedTips, setCompletedTips] = useState<Set<string>>(new Set());
  const [milestones, setMilestones] = useState<Set<string>>(new Set());
  const [currentTip, setCurrentTip] = useState<GuideStep | null>(null);
  const [showGuide, setShowGuide] = useState(false);
  const [tipQueue, setTipQueue] = useState<GuideStep[]>([]);
  const [queueIndex, setQueueIndex] = useState(0);
  const [muted, setMuted] = useState<boolean>(() => {
    try { return localStorage.getItem(MUTED_KEY) === "true"; } catch { return false; }
  });

  // Load persisted state
  useEffect(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved) setCompletedTips(new Set(JSON.parse(saved)));
      const savedM = localStorage.getItem(MILESTONES_KEY);
      if (savedM) setMilestones(new Set(JSON.parse(savedM)));
    } catch {}
  }, []);

  // Fetch user role
  useEffect(() => {
    if (!user) { setUserRole(null); return; }
    supabase.from("profiles").select("role, created_at").eq("id", user.id).single()
      .then(({ data }) => {
        if (data) {
          setUserRole(data.role as UserRole);
          const created = new Date(data.created_at);
          setIsNewUser(Date.now() - created.getTime() < 24 * 60 * 60 * 1000);
        }
      });
  }, [user]);

  // Filter tips based on milestones
  const filterByMilestones = useCallback((tips: GuideStep[]) => {
    return tips.filter((t) => {
      if (t.requiresMilestone && !milestones.has(t.requiresMilestone)) return false;
      if (t.requiresMilestoneAbsent && milestones.has(t.requiresMilestoneAbsent)) return false;
      return true;
    });
  }, [milestones]);

  // Compute page tip stats
  const allPageTips = userRole ? (TIPS[location.pathname]?.[userRole] || []) : [];
  const totalTipsForPage = allPageTips.length;
  const completedTipsForPage = allPageTips.filter((t) => completedTips.has(t.id)).length;

  // Build tip queue when route or role changes
  useEffect(() => {
    if (!userRole || !user) { setTipQueue([]); setCurrentTip(null); return; }

    const path = location.pathname;
    // Mute completely on /chat — assistant does not interrupt active learning
    if (path === "/chat") {
      setTipQueue([]);
      setCurrentTip(null);
      setShowGuide(false);
      return;
    }

    const roleTips = TIPS[path]?.[userRole] || [];
    const filtered = filterByMilestones(roleTips).filter((t) => !completedTips.has(t.id));

    setTipQueue(filtered);
    setQueueIndex(0);

    // Guide suspended: tips no longer auto-display on route change.
    // User must click the lightbulb to see them.
    if (filtered.length > 0) {
      setCurrentTip(null);
      setShowGuide(false);
    } else {
      setCurrentTip(null);
    }
  }, [location.pathname, userRole, user, completedTips, filterByMilestones]);

  // Inactivity detection suspended — tips only show when user clicks the lightbulb.

  const persistCompleted = useCallback((next: Set<string>) => {
    setCompletedTips(next);
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify([...next])); } catch {}
  }, []);

  const persistMilestones = useCallback((next: Set<string>) => {
    setMilestones(next);
    try { localStorage.setItem(MILESTONES_KEY, JSON.stringify([...next])); } catch {}
  }, []);

  const completeTip = useCallback((id: string) => {
    const next = new Set(completedTips);
    next.add(id);
    persistCompleted(next);

    // "Compris" mutes future auto-tips — user can still re-open via the lightbulb.
    setMuted(true);
    try { localStorage.setItem(MUTED_KEY, "true"); } catch {}
    setCurrentTip(null);
    setShowGuide(false);
  }, [completedTips, persistCompleted]);

  const dismissTip = useCallback(() => {
    if (currentTip) completeTip(currentTip.id);
  }, [currentTip, completeTip]);

  const toggleGuide = useCallback(() => {
    setShowGuide((v) => {
      const next = !v;
      // Re-opening from the lightbulb un-mutes the assistant and loads the first tip.
      if (next) {
        setMuted(false);
        try { localStorage.setItem(MUTED_KEY, "false"); } catch {}
        setCurrentTip((prev) => prev ?? (tipQueue[queueIndex] || null));
      }
      return next;
    });
  }, [tipQueue, queueIndex]);

  const trackMilestone = useCallback((m: Milestone) => {
    if (milestones.has(m)) return;
    const next = new Set(milestones);
    next.add(m);
    persistMilestones(next);
  }, [milestones, persistMilestones]);

  const showPostAction = useCallback((tip: GuideStep) => {
    if (completedTips.has(tip.id)) return;
    setCurrentTip(tip);
    setShowGuide(true);
  }, [completedTips]);

  // Auto-complete any tip whose action.path matches the current route
  // → "une fois la fonctionnalité accédée, ne plus la proposer"
  useEffect(() => {
    const path = location.pathname;
    const toComplete: string[] = [];
    Object.values(TIPS).forEach((roleMap) => {
      Object.values(roleMap).forEach((tips) => {
        tips.forEach((t) => {
          if (t.action?.path === path && !completedTips.has(t.id)) {
            toComplete.push(t.id);
          }
        });
      });
    });
    if (toComplete.length > 0) {
      const next = new Set(completedTips);
      toComplete.forEach((id) => next.add(id));
      persistCompleted(next);
    }
  }, [location.pathname, completedTips, persistCompleted]);

  return (
    <GuideContext.Provider value={{
      currentTip, dismissTip, completeTip, completedTips, showGuide, toggleGuide,
      userRole, isNewUser, trackMilestone, milestones,
      totalTipsForPage, completedTipsForPage, showPostAction,
    }}>
      {children}
    </GuideContext.Provider>
  );
};

export const useGuide = () => useContext(GuideContext);
